import { useEffect, useState } from "react";
import { Loader as Loader2 } from "lucide-react";
import type { Opportunity } from "@/hooks/useOpportunities";
import { useCurrency } from "@/hooks/useCurrency";
import { useTranslation } from "react-i18next";
import { MobileSheet } from "./MobileSheet";
import { MobileFieldLabel, mobileInputClass } from "./MobileSelect";

interface MobileOpportunityEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity: Opportunity | null;
  onSave: (updates: Partial<Opportunity>) => Promise<boolean>;
}

const centsToDisplay = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const MobileOpportunityEditSheet = ({
  open,
  onOpenChange,
  opportunity,
  onSave,
}: MobileOpportunityEditSheetProps) => {
  const { t } = useTranslation("crm");
  const { symbol } = useCurrency();
  const [title, setTitle] = useState("");
  const [product, setProduct] = useState("");
  const [priceCents, setPriceCents] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !opportunity) return;
    setTitle(opportunity.title || "");
    setProduct(opportunity.product || "");
    setPriceCents(Math.round((opportunity.price || 0) * 100));
    setNotes(opportunity.notes || "");
    setError(null);
  }, [open, opportunity]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError(t("edit.errTitleRequired"));
      return;
    }

    setSaving(true);
    setError(null);

    const ok = await onSave({
      title: title.trim(),
      product: product.trim(),
      price: priceCents / 100,
      notes: notes.trim() || null,
    });

    setSaving(false);

    if (ok) {
      onOpenChange(false);
    } else {
      setError(t("edit.errSave"));
    }
  };

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange} title={t("edit.title")}>
      <div className="flex flex-col gap-4 pb-2">
        {error && (
          <p className="rounded-xl bg-red-500/10 px-3 py-2.5 text-[13px] text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <MobileFieldLabel>{t("edit.titleRequired")}</MobileFieldLabel>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("contact.titlePlaceholder")}
            className={mobileInputClass}
          />
        </div>

        <div className="flex flex-col gap-2">
          <MobileFieldLabel>{t("edit.productService")}</MobileFieldLabel>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder={t("contact.productPlaceholder")}
            className={mobileInputClass}
          />
        </div>

        <div className="flex flex-col gap-2">
          <MobileFieldLabel>{t("addOpportunity.value")}</MobileFieldLabel>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-muted-foreground">
              {symbol}
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={priceCents ? centsToDisplay(priceCents) : ""}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                setPriceCents(digits ? parseInt(digits, 10) : 0);
              }}
              placeholder="0,00"
              className={`${mobileInputClass} pl-10`}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <MobileFieldLabel>{t("edit.notes")}</MobileFieldLabel>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("edit.notesPlaceholder")}
            rows={4}
            className="w-full resize-none rounded-xl border border-border bg-background p-3 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-accent-blue"
          />
        </div>

        <div className="mt-1 flex gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-12 flex-1 rounded-xl border border-border bg-card text-[15px] font-semibold text-foreground active:bg-hover"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex h-12 flex-[1.4] items-center justify-center gap-2 rounded-xl bg-accent-blue text-[15px] font-semibold text-white transition-opacity active:opacity-80 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? t("edit.saving") : t("common.save")}
          </button>
        </div>

        <p className="text-center text-[12px] text-muted-foreground">
          {t("edit.stageHint")}
        </p>
      </div>
    </MobileSheet>
  );
};
